/**
 * RelatedEventsPanel: collapsible list of related alerts.
 *
 * Shows up to 10 related events from GET /v1/alerts/{id}/related.
 * Each row shows: timestamp, fingerprint (mono), zone, outcome, reason badge.
 * Clicking a row navigates to /event/{related.id}.
 *
 * Uses Radix Collapsible for the expand/collapse behavior.
 * Empty state: "No related events in the past 7 days." message.
 */
import { useNavigate } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { useState } from 'react';
import type { AlertRelated } from './schemas';
import { theme } from '@/design/tokens.css';

/** Max related events to display (per fleet-api contract). */
const MAX_RELATED = 10;

/** Props for RelatedEventsPanel. */
interface Props {
  /** Related events from useAlertRelated (may be empty). */
  events: AlertRelated[];
}

/**
 * Format ISO 8601 to a short local time string (HH:MM:SS).
 */
function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Format ISO 8601 to a short local date string.
 */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Single related event row.
 *
 * Renders timestamp, fingerprint, zone, outcome, and reason badge.
 * The entire row is clickable and navigates to /event/{id}.
 */
function RelatedEventRow({ event }: { event: AlertRelated }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/event/${event.id}`);
  };

  const reasonLabel =
    event.reason === 'same_fingerprint' ? 'same fingerprint' : 'same zone same hour';

  const outcomeLabel = event.outcome
    ? {
        turned_away: 'turned away',
        entered: 'entered',
        loitering: 'loitering',
        other: 'other',
      }[event.outcome] ?? event.outcome
    : 'unresolved';

  // Inline-style colors route through theme tokens (vanilla-extract var()
  // references) so the row adopts the active SOC palette in both modes.
  // Hover background uses surface3 because the parent panel sits on surface1.
  return (
    <button
      onClick={handleClick}
      data-testid={`related-event-row-${event.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '4px 8px',
        padding: '10px 12px',
        background: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${theme.colors.border.subtle}`,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = theme.colors.bg.surface2;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <div>
        <div style={{
          fontFamily: theme.fonts.mono,
          fontSize: '11px',
          color: theme.colors.text.secondary,
        }}>
          {formatDate(event.ts)} {formatTime(event.ts)}
        </div>
        <div style={{
          fontSize: '12px',
          color: theme.colors.text.primary,
          marginTop: '2px',
        }}>
          {event.zone_name ?? 'Site-level'} - {outcomeLabel}
        </div>
        {event.fingerprint && (
          <div style={{
            fontFamily: theme.fonts.mono,
            fontSize: '10px',
            color: theme.colors.text.tertiary,
            marginTop: '2px',
          }}>
            {event.fingerprint.slice(0, 12)}...
          </div>
        )}
      </div>
      <span style={{
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '999px',
        background: theme.colors.bg.surface3,
        color: theme.colors.text.secondary,
        alignSelf: 'start',
        whiteSpace: 'nowrap',
      }}>
        {reasonLabel}
      </span>
    </button>
  );
}

/**
 * Collapsible panel listing up to 10 related alert events.
 *
 * Starts open so the user can see related events immediately.
 * The collapse control reduces visual noise for alerts with many related events.
 *
 * @param props - events array from useAlertRelated
 */
export function RelatedEventsPanel({ events }: Props) {
  const [open, setOpen] = useState(true);
  const displayed = events.slice(0, MAX_RELATED);

  return (
    <Collapsible.Root
      open={open}
      onOpenChange={setOpen}
      data-testid="related-events-panel"
      style={{
        borderTop: `1px solid ${theme.colors.border.subtle}`,
      }}
    >
      <Collapsible.Trigger
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '12px 20px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: theme.colors.text.tertiary,
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
        data-testid="related-events-trigger"
      >
        <span>Related Events ({displayed.length})</span>
        {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
      </Collapsible.Trigger>

      <Collapsible.Content data-testid="related-events-content">
        {displayed.length === 0 ? (
          <p
            style={{
              padding: '16px 20px',
              color: theme.colors.text.tertiary,
              fontSize: '13px',
              margin: 0,
            }}
            data-testid="related-events-empty"
          >
            No related events in the past 7 days.
          </p>
        ) : (
          <div>
            {displayed.map((ev) => (
              <RelatedEventRow key={ev.id} event={ev} />
            ))}
          </div>
        )}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
